import { useFsfStore } from '@/components/fsf/stores/fsf.store'
import { usePaxStore } from '@/components/fsf/pax/store'
import { useOrigDestData } from '@/composables/orig-dest/useOrigDestData'
import { TYPE_AIRPORT } from '@/utils/constants/fsf'
import type { IOrigin, TDestin } from '@/types/OrigDest'

const GREAT_BRITAIN = 'GB'

export const useOrigDestDataHelper = () => {
  const fsfStore = useFsfStore()
  const origDestData = useOrigDestData()
  const paxStore = usePaxStore()
  const { locale } = useI18n()

  const setOrigDestFromUrl = async () => {
    await origDestData.fetchOrigDestData(locale.value)

    const preselectedOrigin = fsfStore.origin + (fsfStore.originType || TYPE_AIRPORT)
    const preselectedDestin = fsfStore.destin + (fsfStore.destinType || TYPE_AIRPORT)

    if (fsfStore.origin) {
      origDestData.selectedOrigin.value = origDestData.allOrigins.value[preselectedOrigin] || null
    } else {
      fsfStore.origin = origDestData.selectedOrigin.value?.code || ''
    }

    if (!origDestData.selectedOrigin.value) return

    if (!fsfStore.destin) {
      fsfStore.destin = origDestData.selectedDestin.value?.code || ''
    }

    const selectedDestin = origDestData.allOriginDestinations(preselectedOrigin)[preselectedDestin] || null

    if (!selectedDestin) {
      fsfStore.$reset()

      return
    }

    origDestData.selectedDestin.value = selectedDestin
    setYoungAdultsAvailable()
  }

  const setYoungAdultsAvailable = () => {
    const { selectedOrigin, selectedDestin } = origDestData

    if (!selectedOrigin.value || !selectedDestin.value) {
      paxStore.youngAdultsAvailable = false

      return
    }

    const isGBOrigin = selectedOrigin.value?.countryCode === GREAT_BRITAIN
    const isGBDestin = selectedDestin.value?.countryCode === GREAT_BRITAIN

    if (isGBOrigin || isGBDestin) {
      paxStore.forcedOpen = true
    }

    paxStore.youngAdultsAvailable = !fsfStore.isRoundTrip ? isGBOrigin : isGBOrigin || isGBDestin
  }

  const handleOriginChange = (newOrigin: IOrigin | null) => {
    if (!newOrigin) return

    if (newOrigin.code !== fsfStore.origin) {
      fsfStore.origin = newOrigin.code || ''
      fsfStore.originType = newOrigin.type || TYPE_AIRPORT
      setYoungAdultsAvailable()

      fsfStore.$reset()
    }
  }

  const handleDestinChange = (newDestin: TDestin | null) => {
    if (!newDestin) return

    if (newDestin.code !== fsfStore.destin) {
      fsfStore.destin = newDestin.code || ''
      fsfStore.destinType = newDestin.type || TYPE_AIRPORT
      setYoungAdultsAvailable()

      fsfStore.$reset()
    }
  }

  watch(origDestData.selectedOrigin, (newOrigin) => handleOriginChange(newOrigin))
  watch(origDestData.selectedDestin, (newDestin) => handleDestinChange(newDestin))
  watch(
    () => fsfStore.isRoundTrip,
    () => setYoungAdultsAvailable()
  )

  return { setOrigDestFromUrl }
}
